if (!customElements.get('card-gallery')) {
  customElements.define(
    'card-gallery',
    class CardGallery extends HTMLElement {
      static autoplayDelay = 1500;

      connectedCallback() {
        this.track = this.querySelector('.card-gallery__track');
        this.dots = Array.from(this.querySelectorAll('.card-gallery__dot'));
        if (!this.track || this.dots.length < 2) return;

        this.track.addEventListener(
          'scroll',
          () => {
            this.loadImages();
            requestAnimationFrame(() => this.updateDots());
          },
          { passive: true }
        );
        this.track.addEventListener('touchstart', () => this.loadImages(), { passive: true, once: true });
        this.dots.forEach((dot, index) =>
          dot.addEventListener('click', () => {
            this.loadImages();
            this.goTo(index);
          })
        );

        // Mouse drag on desktop; touch devices swipe natively.
        this.track.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.track.addEventListener('click', this.onClick.bind(this), true);

        // Desktop: hovering the card plays the images; leaving goes back to the first.
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          const card = this.closest('.card-wrapper') || this;
          card.addEventListener('mouseenter', this.startAutoplay.bind(this));
          card.addEventListener('mouseleave', this.stopAutoplay.bind(this));
        }
      }

      disconnectedCallback() {
        this.stopAutoplay();
      }

      // Later images ship without src (see card-product.liquid) and download on first interaction.
      loadImages() {
        if (this.imagesLoaded) return;
        this.imagesLoaded = true;
        this.track.querySelectorAll('img[data-src]').forEach((img) => {
          img.loading = 'eager';
          if (img.dataset.srcset) img.srcset = img.dataset.srcset;
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          img.removeAttribute('data-srcset');
        });
      }

      startAutoplay() {
        this.loadImages();
        this.showNext();
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = setInterval(() => this.showNext(), CardGallery.autoplayDelay);
      }

      stopAutoplay() {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
        if (this.track) this.track.scrollTo({ left: 0, behavior: 'auto' });
      }

      showNext() {
        const next = this.currentIndex + 1;
        if (next >= this.dots.length) {
          this.track.scrollTo({ left: 0, behavior: 'auto' });
        } else {
          this.goTo(next);
        }
      }

      get currentIndex() {
        return Math.round(this.track.scrollLeft / this.track.clientWidth);
      }

      goTo(index) {
        const last = this.dots.length - 1;
        const target = Math.max(0, Math.min(index, last));
        this.track.scrollTo({ left: target * this.track.clientWidth, behavior: 'smooth' });
      }

      updateDots() {
        const current = this.currentIndex;
        this.dots.forEach((dot, index) => dot.setAttribute('aria-current', index === current));
      }

      onPointerDown(event) {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.loadImages();
        this.dragStartX = event.clientX;
        this.dragStartIndex = this.currentIndex;
        this.dragged = false;

        const onMove = (moveEvent) => {
          if (Math.abs(moveEvent.clientX - this.dragStartX) > 5) this.dragged = true;
        };
        const onUp = (upEvent) => {
          document.removeEventListener('pointermove', onMove);
          document.removeEventListener('pointerup', onUp);
          const distance = upEvent.clientX - this.dragStartX;
          if (Math.abs(distance) > 40) this.goTo(this.dragStartIndex + (distance < 0 ? 1 : -1));
        };
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', onUp);
      }

      onClick(event) {
        // A drag should not open the product.
        if (!this.dragged) return;
        event.preventDefault();
        event.stopPropagation();
        this.dragged = false;
      }
    }
  );
}
